import { memo, useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBug,
  FaLightbulb,
  FaQuestionCircle,
  FaHandshake,
  FaLock,
  FaChevronRight,
  FaHeadset,
  FaClock,
  FaShieldAlt,
  FaUserCheck,
} from "react-icons/fa";
import { useAppContext } from "../../Context/AppContext";
import { isBusinessOrExecutiveMembership } from "../../utils/membershipTier";
import { HELP_CATEGORIES, type HelpCategoryId } from "./helpCategories";

const ICONS: Record<HelpCategoryId, ReactNode> = {
  bug: <FaBug aria-hidden />,
  improvement: <FaLightbulb aria-hidden />,
  general: <FaQuestionCircle aria-hidden />,
  collaboration: <FaHandshake aria-hidden />,
};

type HelpHubProps = {
  onSelect: (id: HelpCategoryId) => void;
};

function HelpHub({ onSelect }: HelpHubProps) {
  const { t } = useTranslation();
  const { user } = useAppContext();

  const firstName = user?.user?.firstName?.trim();

  const canAccessPro = useMemo(
    () => isBusinessOrExecutiveMembership(user?.user?.membershipName),
    [user?.user?.membershipName]
  );

  const handleClick = useCallback(
    (id: HelpCategoryId, isLocked: boolean) => {
      if (isLocked) return;
      onSelect(id);
    },
    [onSelect]
  );

  return (
    <div className="card shadow border-0 rounded overflow-hidden">
      <div className="card-header bg-project-gradient text-white border-0 px-4 py-4">
        <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 flex-shrink-0 mx-auto mx-sm-0"
            style={{ width: 54, height: 54 }}
          >
            <FaHeadset size={25} aria-hidden style={{ display: "block" }} />
          </span>
          <div className="flex-grow-1 text-center text-sm-start">
            <p className="mb-1 small text-white-50 text-uppercase fw-semibold">
              {t("help.hub.eyebrow")}
            </p>
            <h4 className="mb-1 fw-bold">
              {firstName
                ? t("help.hub.greeting", { name: firstName })
                : t("help.hub.title")}
            </h4>
            <p className="mb-0 opacity-75 ">{t("help.hub.subtitle")}</p>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top border-white border-opacity-25">
          <span className="d-inline-flex align-items-center gap-2 rounded-pill border border-white border-opacity-25 bg-white bg-opacity-10 text-white small px-3 py-2">
            <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 p-1">
              <FaClock size={10} aria-hidden />
            </span>
            <span className="fw-semibold">{t("help.responseExpectation")}</span>
          </span>
          <span className="d-inline-flex align-items-center gap-2 rounded-pill border border-white border-opacity-25 bg-white bg-opacity-10 text-white small px-3 py-2">
            <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 p-1">
              <FaShieldAlt size={10} aria-hidden />
            </span>
            <span className="fw-semibold">{t("help.hub.secureBadge")}</span>
          </span>
        </div>
      </div>

      <div className="card-body bg-body-tertiary p-3 p-md-4">
        <div className="mb-3">
          <h6 className="mb-0 fw-semibold text-dark">
            {t("help.hub.chooseHeading")}
          </h6>
          <p className="small text-muted mb-0">
            {t("help.hub.chooseCategory")}
          </p>
        </div>

        <div className="row g-3">
          {HELP_CATEGORIES.map((category) => {
            const isLocked = !!category.proOnly && !canAccessPro;

            return (
              <div key={category.id} className="col-12 col-md-6">
                <button
                  type="button"
                  className="btn w-100 h-100 text-start p-0 border-0 bg-transparent"
                  onClick={() => handleClick(category.id, isLocked)}
                  disabled={isLocked}
                  aria-disabled={isLocked}
                >
                  <div
                    className={`card h-100 rounded-4 border-0 shadow-sm ${
                      isLocked ? "bg-secondary-subtle opacity-75" : "bg-white"
                    }`}
                  >
                    <div className="card-body p-3 p-md-4 d-flex flex-column">
                      <div className="mb-3">
                        <span
                          className={`d-inline-flex align-items-center justify-content-center rounded-4 p-3 fs-4 ${
                            isLocked
                              ? "bg-secondary-subtle text-secondary"
                              : "bg-primary-subtle text-primary"
                          }`}
                        >
                          {isLocked ? (
                            <FaLock aria-hidden />
                          ) : (
                            ICONS[category.id]
                          )}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <h6
                          className={`mb-0 fw-semibold ${
                            isLocked ? "text-secondary" : "text-dark"
                          }`}
                        >
                          {t(category.titleKey)}
                        </h6>
                        {category.proOnly && (
                          <span
                            className={`badge rounded-pill ${
                              isLocked
                                ? "text-bg-secondary"
                                : "text-bg-success"
                            }`}
                          >
                            {isLocked ? (
                              <>
                                <FaLock
                                  size={9}
                                  className="me-1"
                                  aria-hidden
                                />
                                {t("help.locked")}
                              </>
                            ) : (
                              t("help.proOnly")
                            )}
                          </span>
                        )}
                      </div>

                      <p className="small text-muted mb-3 flex-grow-1">
                        {isLocked
                          ? t("help.lockedDesc")
                          : t(category.descKey)}
                      </p>

                      <div
                        className={`d-flex align-items-center justify-content-between small fw-semibold mt-auto pt-2 border-top ${
                          isLocked ? "text-secondary" : "text-primary"
                        }`}
                      >
                        <span>
                          {isLocked
                            ? t("help.hub.unavailable")
                            : t("help.hub.continue")}
                        </span>
                        {!isLocked && <FaChevronRight aria-hidden />}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        <div className="alert alert-light border rounded-4 d-flex align-items-start gap-2 mb-0 mt-4">
          <FaUserCheck className="text-success mt-1 flex-shrink-0" aria-hidden />
          <div className="small mb-0 text-muted">{t("help.hub.footer")}</div>
        </div>
      </div>
    </div>
  );
}

HelpHub.displayName = "HelpHub";

export default memo(HelpHub);
